import { MassChannel } from '#/shared/api/generated/communication/models'
import type { MassRecipientInput } from '#/shared/api/generated/communication/models'
import { isValidEmail } from '#/shared/lib/format'
import { EMPTY_SMS_DELIVERABILITY } from '#/shared/lib/sms-preview'
import type { SmsDeliverability } from '#/shared/lib/sms-preview'
import { MASS_REMOVED_REASON } from '#/shared/ui/mass-recipients'
import type { MassRemovedRecipient } from '#/shared/ui/mass-recipients'

export interface MassPreviewResult {
  totalSelected: number
  recipientCount: number
  removedCount: number
  removed: MassRemovedRecipient[]
  /** Content lint + blast-level gates for the body (SMS only; the local
   *  fallback and the email preview carry no issues). */
  deliverability: SmsDeliverability
}

type PreviewChannel = typeof MassChannel.sms | typeof MassChannel.email

/** Digits-only US phone, 11-digit `1…` collapsed to 10; null when unusable. */
function normalizePhone(contact: string): string | null {
  const digits = contact.replace(/\D/g, '')
  const national =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  return national.length === 10 ? national : null
}

function normalizeContact(
  channel: PreviewChannel,
  contact: string,
): string | null {
  if (channel === MassChannel.email) {
    const email = contact.trim().toLowerCase()
    return isValidEmail(email) ? email : null
  }
  return normalizePhone(contact)
}

/**
 * Client-side recipient curation for the composer dialogs: flags unusable
 * contacts and duplicates (same normalized contact + shipment) before sending.
 * The backend applies the same dedup plus the opt-out filter on send — this
 * mirrors the removed `POST /send/bulk/preview` counts without a round trip.
 */
export function computeMassPreview(
  channel: PreviewChannel,
  recipients: MassRecipientInput[],
): MassPreviewResult {
  const seen = new Set<string>()
  const removed: MassRemovedRecipient[] = []
  let deliverable = 0

  recipients.forEach((recipient, index) => {
    const normalized = normalizeContact(channel, recipient.contact)
    const key =
      normalized === null
        ? null
        : `${normalized}|${recipient.shipment_id ?? ''}`
    const reason =
      key === null
        ? MASS_REMOVED_REASON.invalid
        : seen.has(key)
          ? MASS_REMOVED_REASON.duplicate
          : null
    if (key !== null) seen.add(key)
    if (reason === null) {
      deliverable += 1
      return
    }
    removed.push({
      id: `${recipient.contact}-${index}`,
      name: recipient.name ?? '',
      contact: recipient.contact,
      reason,
      // Local fallback lists one row per shipment (no collapse), so always 1.
      count: 1,
    })
  })

  return {
    totalSelected: recipients.length,
    recipientCount: deliverable,
    removedCount: removed.length,
    removed,
    // Only the server lints the body; while the round trip is in flight the
    // composer shows no findings rather than guessing at them.
    deliverability: EMPTY_SMS_DELIVERABILITY,
  }
}
